import { Grid, MenuItem, MenuList, Typography, Chip, Box } from '@mui/material';
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { makeStyles } from 'tss-react/mui';
import { StatusChip } from '../../CustomComponents/StatusChip';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { CustomGrid, CustomTooltip } from '@AMIEWEB/Common';
import { RowContainer } from '@AMIEWEB/Placement/PlacementDetails/styles';
import { selectInterviewInfo } from 'store/redux-store/placement-summary/selectors';
import { useSelector } from 'react-redux';
import { VirtualInterviewHistory } from './VirtualInterviewHistory';
import { missingField } from 'app/constants';
import { ICandidateInterview } from '@AMIEWEB/Candidate/CandidateProfile/CandidateTabPanel/InterviewsTab/InterviewsTab';
import { disciplineDataSelector } from 'store/redux-store/lookup/lookup.selectors';
import {
  colorTokenMap,
  RenderListWithTooltip,
  RenderWithTooltip,
} from '@AMIEWEB/Candidate/CandidateProfile/CandidateTabPanel/InterviewsTab/InterviewCard/InterviewCard';
import { theme } from 'styles/global-styles';
import moment from 'moment';
import { convertToPST } from 'app/helpers/dateHelper';
import { GenericDialog } from '@AMIEWEB/Alerts/GenericDialog';
import { getEmploymentHistoryHeaders } from '@AMIEWEB/Candidate/CandidateProfile/CandidateTabPanel/InterviewsTab/helpers';

const defaultHistoryProps = {
  rows: [],
  open: false,
  inviteId: undefined,
  interviewDate: undefined,
};
export interface IVICardView {
  skillset;
  reason;
  rating;
  status;
  interviewDateValue;
  sentDateValue;
  lastUpdatedValue;
  inviteId;
  history;
  chipStyle;
}

const useItemStyles = makeStyles()(() => ({
  container: {
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'nowrap',
    justifyContent: 'space-between',
    gap: 24,
  },
  titleCell: {
    textAlign: 'start',
    fontWeight: 500,
  },
  actionColumn: {
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'nowrap',
    height: 36,
    gap: 12,
  },
  headCell: {
    fontSize: 12,
    fontWeight: 450,
  },
  cardHeadCell: {
    minWidth: 125,
  },
  valueCell: {
    fontSize: 14,
  },
  tooltip: {
    padding: '12px 0',
  },
  chip: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    width: 'fit-content',
    whiteSpace: 'pre',
    fontWeight: 400,
  },
  dialogContainer: {
    width: '80%',
    maxWidth: '80%',
  },
  dialogContentRoot: {
    padding: '1.5rem',
    fontWeight: 500,
  },
}));

export interface IVIInterviewSubCardItemProps {
  interViewDetails: ICandidateInterview;
}

export const RenderToolTipListWithTooltip = ({
  values,
  maxLength = 30,
  className,
}: {
  values?: string[];
  maxLength?: number;
  className?: string;
}) => {
  if (!values || values.length === 0) {
    return <Box className={className}>{missingField}</Box>;
  }

  const joined = values.join(', ');
  const shouldTruncate = joined.length >= maxLength;

  const tooltipContent = (
    <ul style={{ margin: 0, paddingLeft: 16 }}>
      {values.map((item, index) => (
        <li key={index} style={{ fontSize: '12px', color: 'black' }}>
          {item}
        </li>
      ))}
    </ul>
  );

  return shouldTruncate ? (
    <CustomTooltip
      contentStyle={{
        overflow: 'hidden',
        color: '#000',
        fontSize: '14px',
        fontWeight: 'normal',
      }}
      tooltipContent={tooltipContent}
      value={`${joined.slice(0, maxLength - 7)}...`}
    />
  ) : (
    <Box className={className}>{joined}</Box>
  );
};

const ScheduleItem: React.FC<IVIInterviewSubCardItemProps> = ({ interViewDetails }) => {
  const { classes } = useItemStyles();
  const { t } = useTranslation();
  const [isDialogOpen, setDialogOpen] = useState<boolean>(false);
  const [dialogData, setDialogData] = useState<ICandidateInterview | null>();

  const getRows = data => {
    // eslint-disable-next-line array-callback-return
    const interviewRows: any = [];
    if (dialogData) {
      data.history.forEach(item => {
        const row = {
          fieldChanged: (
            <span>
              {`${item?.inviteId ? `${item.inviteId} - ` : ''}`}
              <span style={{ fontWeight: 'bold' }}>{item.field}</span>
            </span>
          ),
          oldValue: item.oldValue,
          newValue: item.newValue,
          changedBy: item.changedBy,
          date: `${convertToPST(item.changedDate)}`,
        };
        interviewRows.push(row);
      });
    }
    return interviewRows;
  };

  const handleClick = (data: ICandidateInterview) => {
    setDialogData(data);
    setDialogOpen(true);
  };

  const getSkillset = () => {
    const renderText = interViewDetails?.discipline
      ? interViewDetails?.specialtyAbbr
        ? `${interViewDetails?.discipline} - ${interViewDetails?.specialtyAbbr}`
        : interViewDetails?.discipline
      : missingField;
    return <RenderWithTooltip text={renderText} maxLength={30} className={classes.titleCell} />;
  };

  const chipData = {
    color: colorTokenMap[interViewDetails?.candidateInterviewStatus?.color] || colorTokenMap.tertiaryGrey,
    background: colorTokenMap[interViewDetails?.candidateInterviewStatus?.background] || colorTokenMap.platinum,
    label: interViewDetails?.candidateInterviewStatus?.label || 'Unavailable',
  };

  const mostRecentReason = interViewDetails?.candidateInterviewReasons?.find(r => r.isMostRecent);
  const { tooltipContent } = useMemo(() => {
    return {
      tooltipContent: (
        <RowContainer>
          <tr>
            <td className={classes.headCell}>
              {t('placement.profile.summary.interview.virtualInterviewContainer.interviewSpecs.interviewDate')}
            </td>
            <td className={classes.valueCell}>
              {interViewDetails?.interviewDate
                ? moment(new Date(interViewDetails.interviewDate)).format('MM/DD/YYYY')
                : missingField}
            </td>
          </tr>
          <tr>
            <td className={classes.headCell}>
              {t('placement.profile.summary.interview.virtualInterviewContainer.interviewSpecs.sentDate')}
            </td>
            <td className={classes.valueCell}>
              {interViewDetails?.invitationDate
                ? moment(new Date(interViewDetails.invitationDate)).format('MM/DD/YYYY')
                : missingField}
            </td>
          </tr>
          <tr>
            <td className={classes.headCell}>
              {t('placement.profile.summary.interview.virtualInterviewContainer.interviewSpecs.inviteId')}
            </td>
            <td className={classes.valueCell}>{interViewDetails?.inviteId ?? missingField}</td>
          </tr>
        </RowContainer>
      ),
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [interViewDetails]);

  return (
    <div className={classes.container}>
      <table>
        <tbody>
          <tr>
            <td colSpan={2} className={classes.titleCell}>
              <CustomTooltip isStandardContent standardMargin tooltipContent={tooltipContent} placement="bottom">
                {getSkillset()}
              </CustomTooltip>
            </td>
          </tr>
          <tr>
            <td className={`${classes.headCell} ${classes.cardHeadCell}`}>
              {t('placement.profile.summary.interview.virtualInterviewContainer.interviewSpecs.lastUpdated')}
            </td>
            <td className={classes.valueCell}>
              {interViewDetails?.changedDate
                ? moment(new Date(interViewDetails.changedDate)).format('MM/DD/YYYY')
                : missingField}
            </td>
          </tr>
          <tr>
            <td className={`${classes.headCell} ${classes.cardHeadCell}`}>
              {t('placement.profile.summary.interview.virtualInterviewContainer.interviewSpecs.rating')}
            </td>
            <td className={classes.valueCell}>
              {' '}
              {interViewDetails?.interviewRating !== ''
                ? interViewDetails?.interviewRating?.substring(0, interViewDetails?.interviewRating?.length - 1)
                : missingField}
            </td>
          </tr>
          <tr>
            <td className={classes.headCell}>{t('candidate.virtualInterviewTabLabels.statusReason')}</td>
            <td className={classes.valueCell}>{mostRecentReason?.reason ?? missingField}</td>
          </tr>
          <tr>
            <td className={classes.headCell}>{t('candidate.virtualInterviewTabLabels.subStatus')}</td>
            <td className={classes.valueCell}>
              <RenderToolTipListWithTooltip values={mostRecentReason?.subReasons} />
            </td>
          </tr>
        </tbody>
      </table>
      <div className={classes.actionColumn}>
        <CustomTooltip
          tooltipContent={interViewDetails?.candidateInterviewStatus?.interviewStatus ?? ''}
          interactive
          arrow
        >
          <Chip
            className={classes.chip}
            style={{
              color: chipData?.color,
              borderColor: chipData?.color,
              background: chipData?.background,
            }}
            variant="outlined"
            size="medium"
            label={chipData?.label}
          />
        </CustomTooltip>
        <CustomTooltip
          standardMargin
          classes={{ tooltip: classes.tooltip }}
          contentStyle={{ alignSelf: 'center' }}
          tooltipContent={
            <TooltipContent
              onClick={() => {
                handleClick(interViewDetails);
              }}
            >
              {t('placement.profile.summary.interview.virtualInterviewContainer.interviewSpecs.history.viewHistory')}
            </TooltipContent>
          }
          placement={'bottom'}
        >
          <MoreVertIcon />
        </CustomTooltip>
        <GenericDialog
          variant={'blue'}
          fullWidth
          classes={{
            paper: classes.dialogContainer,
          }}
          open={isDialogOpen}
          disablePortal
          disableEscapeKeyDown={false}
          dialogTitleProps={{
            text: `Virtual Interview - View History`,
            closeButton: true,
          }}
          onClose={() => setDialogOpen(false)}
          dialogContentProps={{
            classes: { root: classes.dialogContentRoot },
          }}
        >
          <>
            <Box>
              {`Invite: ${dialogData?.inviteId ? dialogData.inviteId : '-'}`}
              {' ,  '}
              {`Date: ${
                dialogData?.interviewDate ? moment(new Date(dialogData.interviewDate)).format('MM/DD/YYYY') : '--'
              }`}
            </Box>
            <CustomGrid
              columns={getEmploymentHistoryHeaders()}
              rows={getRows(dialogData)}
              gridName={'InterviewHistoryGrid'}
              defaultPageView={'25'}
              fixedItemsPerPage={25}
              pageOptions={[25, 50, 100, 200]}
              disableGutters
              noPadding
              centerHoverView={false}
              showCount={false}
              minCellWidth={'100px'}
            />
          </>
        </GenericDialog>
      </div>
    </div>
  );
};

export const InterviewSpecs = () => {
  const { t } = useTranslation();
  const {
    virtualInterview: { candidate: interviews },
  } = useSelector(selectInterviewInfo);
  const [historyModalProps, setHistoryModalProps] = useState(defaultHistoryProps);

  return !interviews.length ? (
    <Typography variant="body1">
      {t('placement.profile.summary.interview.virtualInterviewContainer.interviewSpecs.skillNotMatching')}
    </Typography>
  ) : (
    <Grid container spacing={2}>
      {interviews.map((cardView, index) => (
        <Grid item key={index.toString()} xs={12}>
          <ScheduleItem interViewDetails={cardView} />
        </Grid>
      ))}
      <VirtualInterviewHistory {...historyModalProps} onClose={() => setHistoryModalProps(defaultHistoryProps)} />
    </Grid>
  );
};

const TooltipContent = ({ onClick, ...props }) => {
  return (
    <MenuList disablePadding>
      <MenuItem dense onClick={onClick}>
        {props.children}
      </MenuItem>
    </MenuList>
  );
};
